import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { AlertSearchPayload } from 'app/modules/search/payload';
import {
  AlertSearchV2Response,
  AlertSource,
} from 'app/modules/search/response';
import { withSearchErrorToast } from 'app/modules/search/utils';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { post } from 'app/shared/utils/fetchr';
import { unionBy } from 'lodash';
import { useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';

const getAlertSources = (data: AlertSearchV2Response): AlertSource[] =>
  data.hits.map((hit) => hit.source);

export const searchAlert = (payload?: AlertSearchPayload) =>
  post<AlertSearchV2Response>('/v1/alerts/search/v2', payload);

export const useAlertSearch = (
  payload?: AlertSearchPayload,
  enabled: boolean = true,
) => {
  const queryClient = useQueryClient();
  const hasReadAlertsPermissions = useSelector(selectHasReadAlertsPermission);

  const query = useQuery<AlertSearchV2Response, Error, AlertSource[]>({
    enabled: Boolean(enabled && !!payload && hasReadAlertsPermissions),
    // can't garbage collect since we return all transactions in the query history
    gcTime: Infinity,
    queryFn: withSearchErrorToast(() => searchAlert(payload)),
    queryKey: ALERT_QUERY_KEYS.getAlertSearch(payload),
    select: (data) => getAlertSources(data),
    staleTime: 5 * 60 * 1000,
  });

  // call to manually refresh alerts from cached data
  const [, refreshAlerts] = useReducer((x) => x + 1, 0);

  const cachedQueryData = queryClient.getQueriesData<AlertSearchV2Response>({
    queryKey: ALERT_QUERY_KEYS.getAlertSearch(),
  });

  const alerts = useMemo(
    () =>
      cachedQueryData.reduce<AlertSource[]>((acc, i) => {
        const [, queryData] = i;
        if (queryData) {
          return unionBy(getAlertSources(queryData), acc, 'id');
        }
        return acc;
      }, []),

    [cachedQueryData],
  );

  return {
    query,
    alerts,
    refreshAlerts,
  };
};
